"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Alert } from "../../alert";
import { LeerZustand } from "../../leerzustand";
import { DateiAblage, Suchfeld, Textfeld, useDateiZiehen } from "../../formular";
import { KleinesModal } from "../../modal/kleines-modal";
import { SymBild } from "../lesen/lese-symbole";

/*
 * Bild-Ablage des Editors (G-W6 Variante A, Betreiber 13.10.2026, Musterkatalog M-052, Design-Guide `editor-einfassung.md`):
 * ein Kleines Modal M mit Ablagezone, Suche, Raster mit Auswahl, Alternativtext und „Einfügen“. Hochladen nur mit
 * `darfHochladen` (ohne ist die Zone nicht da, r110); die Dateizone ist `DateiAblage` (M-011 Teil 3, Zeilenform, ziehen und ablegen
 * über dem ganzen Modal-Körper). Abweichung AW-069: das Bild zeigt eine große Ablagefläche, hier die Geist-Zeile des Bausteins.
 *
 * Der Baustein prüft weder Rechte noch Dateityp noch Größe: Liste, Hochladen und Einfügen sind Funktionen der Seite (Wiki: die
 * bestehenden Actions `listeWikiBilderAction`, `hochladenWikiBildAction`, der Befehl `einfuegen.bild`). Die Sätze kommen vom Aufrufer,
 * nie ein Code (r105): `hinweis` = behebbar (andere Datei wählen, Pille an der Zone), `ablehnung` und `fehlschlag` = Alert im Modal,
 * der das Modal überlebt (r98: das Modal bleibt offen, die Auswahl bleibt). Es wird nichts gemessen (r260).
 */

export interface BildAblageBild {
  id: string;
  /** Dateiname, wie er in der Kachel steht. */
  name: string;
  /** Adresse der Auslieferung (Wiki: `/api/wiki/media?key=…`); folgt dem Recht auf den Raum. */
  url: string;
}

export type BildAblageSatz = { art: "hinweis" | "ablehnung" | "fehlschlag"; text: string };

export type BildAblageListe = { ok: true; bilder: BildAblageBild[] } | ({ ok: false } & BildAblageSatz);
export type BildAblageHochgeladen = { ok: true; bild: BildAblageBild } | ({ ok: false } & BildAblageSatz);

export const BILDABLAGE_TEXTE = {
  titel: "Bild einfügen",
  einfuegen: "Einfügen",
  zone: "Datei auswählen",
  zoneAblegen: "Loslassen zum Hochladen",
  zoneHilfe: "JPG, PNG, WebP · bis 10 MB",
  suche: "Bilder suchen …",
  sucheLabel: "Bilder dieses Raums durchsuchen",
  leerTitel: "Noch keine Bilder in diesem Raum",
  leerMitRecht: "Ziehen Sie ein Bild hierher, um es hochzuladen.",
  leerOhneRecht: "Bilder, die in diesem Raum hochgeladen werden, erscheinen hier.",
  keineTreffer: "Keine Bilder gefunden.",
  keineTrefferText: "Bitte einen anderen Suchbegriff versuchen.",
  laedt: "Lädt …",
  laedtHoch: "Wird hochgeladen …",
  nichtsGewaehlt: "Kein Bild ausgewählt",
  gewaehlt: "1 Bild ausgewählt",
  altLabel: "Alternativtext",
  altHilfe: "Beschreibt das Bild für Screenreader und wenn es nicht lädt. Leer lassen, wenn es nur schmückt.",
  nichtEinfuegbar: "Dieses Bild lässt sich nicht einfügen. Bitte ein anderes Bild wählen.",
  keineWahl: "Bitte zuerst ein Bild wählen.",
  /** Nur Rückfall, falls der Aufrufer wirft statt einen Satz zu liefern (r105: über den Vorgang, wiederholbar, ohne Code). */
  fehlschlag: "Das hat nicht geklappt. Bitte erneut versuchen.",
} as const;

const ERLAUBTE_TYPEN = "image/jpeg,image/png,image/webp";
const ALT_MAX = 300;
const SUCHE_WARTEZEIT_MS = 250;

/** Dateiname ohne Endung als Vorschlag für den Alternativtext. */
export function altVorschlag(name: string): string {
  const punkt = name.lastIndexOf(".");
  const stamm = punkt > 0 ? name.slice(0, punkt) : name;
  return stamm.replace(/[_-]+/g, " ").trim().slice(0, ALT_MAX);
}

function Kachel({ bild, gewaehlt, beiWahl }: { bild: BildAblageBild; gewaehlt: boolean; beiWahl: () => void }) {
  // Ein Bild, das nicht lädt (404 auch bei fehlendem Recht, r95), zeigt das Platzhaltersymbol, nie einen Grund.
  const [kaputt, setKaputt] = useState(false);
  return (
    <li>
      {/* Preline: data-display/data-display-sub-operational-activity-views/data-display-cat-file-views/image-file-grid-view */}
      <button
        type="button"
        aria-pressed={gewaehlt}
        title={bild.name}
        onClick={beiWahl}
        className={
          gewaehlt
            ? "relative w-full text-start rounded-xl border-2 border-primary bg-layer overflow-hidden focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus"
            : "relative w-full text-start rounded-xl border border-layer-line bg-layer overflow-hidden hover:bg-surface-hover focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus"
        }
      >
        <span className="flex h-24 items-center justify-center bg-surface text-muted-foreground-1">
          {kaputt ? (
            <SymBild />
          ) : (
            <img src={bild.url} alt="" loading="lazy" className="size-full object-cover" onError={() => setKaputt(true)} />
          )}
        </span>
        <span className="block truncate px-2 py-1.5 text-xs text-foreground">{bild.name}</span>
        {gewaehlt && (
          <span className="absolute top-1.5 end-1.5 inline-flex size-5 items-center justify-center rounded-full bg-primary text-primary-foreground" aria-hidden="true">
            <svg className="shrink-0 size-3" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </span>
        )}
      </button>
    </li>
  );
}

export function EditorBildAblage({
  offen,
  raumName,
  darfHochladen,
  laden,
  hochladen,
  beiEinfuegen,
  beiSchliessen,
}: {
  offen: boolean;
  /** Name des Raums, dem die Bilder gehören (Untertitel: „Bilder des Raums …“). */
  raumName: string;
  /** Die Seite entscheidet mit derselben Prüfung wie die Action; ohne Recht gibt es die Zone nicht (r110). */
  darfHochladen: boolean;
  /** Bilder des Raums, optional nach Dateiname gefiltert (Wiki: `listeWikiBilderAction`, still gefiltert auf Lesbares). */
  laden: (suche: string) => Promise<BildAblageListe>;
  /** Eine Datei hochladen (Wiki: `hochladenWikiBildAction`); die Seite prüft vorab Typ und Größe mit dem Wortlaut des Servers. */
  hochladen: (datei: File) => Promise<BildAblageHochgeladen>;
  /** Fügt ein (Wiki: `einfuegen.bild`); `ok: false` lässt das Modal offen. */
  beiEinfuegen: (daten: { src: string; alt: string }) => { ok: true } | { ok: false };
  beiSchliessen: () => void;
}) {
  const [suche, setSuche] = useState("");
  const [liste, setListe] = useState<{ art: "laedt" } | { art: "ok"; bilder: BildAblageBild[] } | { art: "satz"; satz: BildAblageSatz }>({ art: "laedt" });
  const [gewaehlt, setGewaehlt] = useState<BildAblageBild | null>(null);
  const [alt, setAlt] = useState("");
  const [laedtHoch, setLaedtHoch] = useState(false);
  const [uploadSatz, setUploadSatz] = useState<BildAblageSatz | null>(null);
  const lauf = useRef(0);

  const lade = useCallback(
    async (begriff: string) => {
      const nr = ++lauf.current;
      const antwort = await laden(begriff).catch((): BildAblageListe => ({ ok: false, art: "fehlschlag", text: BILDABLAGE_TEXTE.fehlschlag }));
      if (nr !== lauf.current) return;
      setListe(antwort.ok ? { art: "ok", bilder: antwort.bilder } : { art: "satz", satz: antwort });
    },
    [laden],
  );

  // Beim Öffnen ohne Reste der letzten Wahl beginnen; die Liste wird frisch gelesen (ein Bild kann inzwischen entfallen sein).
  useEffect(() => {
    if (!offen) return;
    setSuche("");
    setGewaehlt(null);
    setAlt("");
    setUploadSatz(null);
    setListe({ art: "laedt" });
    void lade("");
    return () => {
      lauf.current += 1;
    };
    // `lade` hängt an `laden` der Seite; das Öffnen löst genau ein Laden aus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offen]);

  // Suche mit kleiner Wartezeit, nie bei jedem Tastendruck eine Anfrage.
  const sucheTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => void (sucheTimer.current && clearTimeout(sucheTimer.current)), []);
  const beiSuche = (wert: string) => {
    setSuche(wert);
    if (sucheTimer.current) clearTimeout(sucheTimer.current);
    sucheTimer.current = setTimeout(() => void lade(wert.trim()), SUCHE_WARTEZEIT_MS);
  };

  const waehle = (bild: BildAblageBild) => {
    setGewaehlt(bild);
    setAlt(altVorschlag(bild.name));
  };

  const dateiHoch = async (dateien: File[]) => {
    const datei = dateien[0];
    if (!datei || laedtHoch) return;
    setLaedtHoch(true);
    setUploadSatz(null);
    try {
      const antwort = await hochladen(datei).catch((): BildAblageHochgeladen => ({ ok: false, art: "fehlschlag", text: BILDABLAGE_TEXTE.fehlschlag }));
      if (!antwort.ok) {
        setUploadSatz({ art: antwort.art, text: antwort.text });
        return;
      }
      // Das neue Bild steht vorn und ist gewählt: der nächste Klick fügt es ein.
      setListe((l) => (l.art === "ok" ? { art: "ok", bilder: [antwort.bild, ...l.bilder.filter((b) => b.id !== antwort.bild.id)] } : { art: "ok", bilder: [antwort.bild] }));
      waehle(antwort.bild);
    } finally {
      setLaedtHoch(false);
    }
  };
  const { ziehend, bereichProps } = useDateiZiehen((d) => void dateiHoch(d), darfHochladen && !laedtHoch);

  const ohneBilder = liste.art === "ok" && liste.bilder.length === 0;
  const sucheAktiv = suche.trim() !== "";

  return (
    <KleinesModal
      offen={offen}
      groesse="m"
      titel={BILDABLAGE_TEXTE.titel}
      beschreibung={`Bilder des Raums ${raumName}`}
      beimSchliessen={beiSchliessen}
      bestaetigenText={BILDABLAGE_TEXTE.einfuegen}
      bestaetigenGesperrt={gewaehlt === null || laedtHoch}
      onBestaetigen={() => {
        if (!gewaehlt) return { ok: false, meldung: BILDABLAGE_TEXTE.keineWahl };
        const ergebnis = beiEinfuegen({ src: gewaehlt.url, alt: alt.trim().slice(0, ALT_MAX) });
        return ergebnis.ok ? { ok: true } : { ok: false, meldung: BILDABLAGE_TEXTE.nichtEinfuegbar };
      }}
    >
      <div className="space-y-4" {...bereichProps} data-tour="wiki-bildablage">
        {darfHochladen && (
          <div className="space-y-2">
            <DateiAblage
              label={BILDABLAGE_TEXTE.zone}
              ablegenText={BILDABLAGE_TEXTE.zoneAblegen}
              hilfe={BILDABLAGE_TEXTE.zoneHilfe}
              accept={ERLAUBTE_TYPEN}
              mehrere={false}
              ziehend={ziehend}
              deaktiviert={laedtHoch}
              fehler={uploadSatz?.art === "hinweis" ? [uploadSatz.text] : []}
              onDateien={(d) => void dateiHoch(d)}
            />
            {laedtHoch && (
              <p className="text-sm text-muted-foreground-1" role="status">
                {BILDABLAGE_TEXTE.laedtHoch}
              </p>
            )}
            {uploadSatz && uploadSatz.art !== "hinweis" && <Alert ton="warnung">{uploadSatz.text}</Alert>}
          </div>
        )}

        {liste.art === "satz" ? (
          <Alert ton="warnung">{liste.satz.text}</Alert>
        ) : (
          <>
            {(!ohneBilder || sucheAktiv) && (
              <Suchfeld
                label={BILDABLAGE_TEXTE.sucheLabel}
                placeholder={BILDABLAGE_TEXTE.suche}
                groesse="standard"
                autoComplete="off"
                maxLength={100}
                value={suche}
                onChange={(e) => beiSuche(e.target.value)}
                // Enter im Suchfeld darf das Modal nicht abschicken.
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.preventDefault();
                }}
              />
            )}
            {liste.art === "laedt" && (
              <p className="text-sm text-muted-foreground-1" role="status">
                {BILDABLAGE_TEXTE.laedt}
              </p>
            )}
            {liste.art === "ok" && liste.bilder.length === 0 && (
              <LeerZustand
                symbol={<SymBild />}
                titel={sucheAktiv ? BILDABLAGE_TEXTE.keineTreffer : BILDABLAGE_TEXTE.leerTitel}
                text={sucheAktiv ? BILDABLAGE_TEXTE.keineTrefferText : darfHochladen ? BILDABLAGE_TEXTE.leerMitRecht : BILDABLAGE_TEXTE.leerOhneRecht}
              />
            )}
            {liste.art === "ok" && liste.bilder.length > 0 && (
              <ul className="grid grid-cols-2 sm:grid-cols-4 gap-2" aria-label="Bilder des Raums">
                {liste.bilder.map((b) => (
                  <Kachel key={b.id} bild={b} gewaehlt={gewaehlt?.id === b.id} beiWahl={() => waehle(b)} />
                ))}
              </ul>
            )}
          </>
        )}

        {gewaehlt && (
          <Textfeld
            label={BILDABLAGE_TEXTE.altLabel}
            hilfe={BILDABLAGE_TEXTE.altHilfe}
            optional
            maxLength={ALT_MAX}
            autoComplete="off"
            value={alt}
            onChange={(e) => setAlt(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.preventDefault();
            }}
          />
        )}
        <p className="text-xs text-muted-foreground-1" aria-live="polite">
          {gewaehlt ? BILDABLAGE_TEXTE.gewaehlt : BILDABLAGE_TEXTE.nichtsGewaehlt}
        </p>
      </div>
    </KleinesModal>
  );
}
